// cartOffer.js
// The offer a call-center agent attaches to a cart link (cart_items.offer):
// { slug, name, for_life, choice, eligible_terms, free_months_by_terms?, line }.
// `line` is the order line exactly as the backend stored it; its terms and planId
// are load-bearing, so it is sent as-is and only its display is reshaped here.
//
// Ported from newself-front's call-center checkout (PR #206).

import { shopImage } from '@/utils/shopImage';

/* The RxPros shop's own cut-out bottles, from its S3 mirror of rxpros-shop/src/assets,
   for the moment before the shop's list lands (utils/shopImage.js). */
const RXPROS_SHOP_ASSETS = 'https://rxpros.s3.us-east-2.amazonaws.com/public/rxpros-shop/assets';
const NAD_NASAL_IMAGE = `${RXPROS_SHOP_ASSETS}/shop/nasal-nad.webp`;
const NAD_INJECTION_IMAGE = `${RXPROS_SHOP_ASSETS}/products/nano_nad_1500/vial-nad-injection.webp`;

/**
 * The offer applies only on the plan lengths it runs on (3/6/12 for free-for-life):
 * on a monthly plan its line and slug both drop out, and come back when the
 * customer picks an eligible plan again.
 */
export const isOfferActive = (offer, terms) =>
    !!(offer && offer.line && Array.isArray(offer.eligible_terms) && offer.eligible_terms.includes(terms));

/** What the offer adds to the charge: 0 for a free line, the promo price for an upgrade. */
export const offerCharge = (offer, terms) =>
    isOfferActive(offer, terms) ? (Number(offer.line.price) || 0) : 0;

/** The order lines plus the offer line, copied so the stored offer is never mutated. */
export const withOfferLine = (lines, offer, terms) =>
    isOfferActive(offer, terms) ? [...lines, { ...offer.line }] : lines;

/**
 * The slug travels only with its line: a line without it is billed, a slug without
 * its line grants nothing. `fallback` is the checkout's own offer value.
 */
export const offerSlug = (offer, terms, fallback) =>
    isOfferActive(offer, terms) ? offer.slug : fallback;

/**
 * The Klarna cart snapshot ([{ id, quantity, price }]) for KlarnaReturn.vue. The
 * offer line is listed on its own, so the lines still add up to the charge.
 */
export const klarnaSnapshotItems = (mainId, chargeCents, offer, terms) => {
    const offerCents = Math.round(offerCharge(offer, terms) * 100);
    const items = [{ id: mainId, quantity: 1, price: Number(((chargeCents - offerCents) / 100).toFixed(2)) }];
    if (isOfferActive(offer, terms)) {
        items.push({ id: offer.line.id, quantity: 1, price: Number((offerCents / 100).toFixed(2)) });
    }
    return items;
};

// Summary display, for CartOfferRow.vue.

/** "NAD+ Nasal 3000mg (3-month bottle)" reads "NAD+ Nasal". The payload keeps the catalog name. */
export const offerLineName = (line) => {
    const name = String((line && line.name) || '');
    return name.replace(/\s*\d[\d,.]*\s*mg\b.*$/i, '').trim() || name;
};

export const offerPriceLabel = (line) => {
    const price = Number(line && line.price) || 0;
    return price > 0 ? `$${price.toFixed(2)}` : 'FREE';
};

/**
 * Under the line's name: the offer for a free line. For the paid upgrade, what it
 * replaces, by the offer's own name: "Free NAD+ Nasal for life" over a $119.97
 * price reads as a contradiction.
 */
export const offerSubtitle = (offer) => {
    if (!offer) return '';
    return offer.choice === 'upgrade' ? `Upgrade — instead of ${offer.name}` : offer.name;
};

/** The note under the subtitle, from the line's own term, never an assumed product. */
export const offerNote = (offer, terms) => {
    if (!offer || !offer.line) return '';
    if (offer.choice === 'upgrade') return `$${Number(offer.line.price).toFixed(2)} every ${offer.line.terms} months`;
    if (offer.for_life) return `FREE for life · ships every ${offer.line.terms} months`;
    const months = Number((offer.free_months_by_terms || {})[String(terms)]) || 1;
    return `FREE for ${months} month${months === 1 ? '' : 's'}`;
};

/**
 * The line's picture: the one shop.rxpros.com shows for its product. Until the shop's
 * list lands, the catalog names NAD+ (internal_id NAD) the injection, and every nasal
 * NAD+ product the spray bottle. Any other product gets no picture and the row shows
 * without one.
 */
export const offerImage = (line) => {
    const shopPicture = shopImage(line && line.id);
    if (shopPicture) return shopPicture;
    const name = String((line && line.name) || '');
    if (/nasal/i.test(name)) return NAD_NASAL_IMAGE;
    if (/^NAD\+/i.test(name)) return NAD_INJECTION_IMAGE;
    return null;
};
